import { PagedResponseDto } from "../../../../shared/dtos/paged-response.dto";
import { BaseService } from "../../../../shared/services";
import { readErrorResponse } from "../../../../shared/services/read-error-response";
import { buildFilterCondition } from "../../../../shared/utils";
import { IObservationPhotoUploadDto } from "../dtos/observation-photo-upload.dto";
import { IObservationUpsertDto } from "../dtos/observation-upsert.dto";
import { IObservationDto } from "../dtos/observation.dto";

class ObservationsService extends BaseService<IObservationDto, never> {
  private path = "ObservationsV3";
  private photosPath = "ObservationPhotosV2";

  async get(
    id: string,
    options: { signal?: AbortSignal } = {}
  ): Promise<IObservationDto> {
    const { signal = null } = options;

    // ObservationsV3 has no GET {id} action, fetch via the list endpoint filtered by id
    // (pageNumber is required when filter is set, see GetPagesQuery.IsBatching)
    // onlyOriginal=true switches the backend to GetBaseQueryFromOriginalTable, which
    // selects Id directly from the Observations table — without it, ids missing from
    // the VObservationPrevs view return zero rows
    const searchParams: Record<string, unknown> = {
      pageNumber: 1,
      pageSize: 1,
      filter: buildFilterCondition("id", "==", id),
      onlyOriginal: true,
    };

    const response = await this.request<PagedResponseDto<IObservationDto>>(this.path, {
      method: "GET",
      searchParams,
      signal,
    });

    const dto = response.data[0];
    if (!dto) {
      throw new Error(`Observation with id=${id} not found`);
    }

    return this.filterIsDeletedPhotos(dto);
  }

  async list(_filter: never): Promise<IObservationDto[]> {
    throw new Error("Not implemented — use observations.rtkq.ts");
  }

  async add(dto: IObservationUpsertDto): Promise<IObservationDto> {
    return this.request<IObservationDto>(this.path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify(dto),
    });
  }

  async update(data: IObservationUpsertDto): Promise<IObservationDto> {
    if (!data?.id) {
      throw new Error(`Can't update entity without id`);
    }

    const updateDto: IObservationUpsertDto = {
      ...data,
      id: undefined,
      cropId: undefined,
    };

    return this.request<IObservationDto>([this.path, data.id].join("/"), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify(updateDto),
    });
  }

  async completeReport(observationId: string): Promise<IObservationDto | undefined> {
    if (!observationId) {
      throw new Error("Can't complete observation report without id");
    }

    const response = await this.request<Response>([this.path, observationId].join("/"), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify({ isReportComplete: true }),
      raw: true,
    });

    if (!response.ok) {
      throw await readErrorResponse(response);
    }

    const body = await response.text();
    return body.trim() ? (JSON.parse(body) as IObservationDto) : undefined;
  }

  async delete(id: string): Promise<unknown> {
    return this.request([this.path, id].join("/"), { method: "DELETE" });
  }

  async uploadPhotos(
    observationId: string,
    appUserId: string,
    files: File[]
  ): Promise<IObservationPhotoUploadDto[]> {
    const data = new FormData();
    files.forEach((file) => {
      data.append("photos", file);
    });

    return this.request([this.path, observationId, "uploadPhotos"].join("/"), {
      method: "POST",
      searchParams: { appUserId },
      body: data,
    });
  }

  async deletePhoto(id: string): Promise<void> {
    return this.request([this.photosPath, id].join("/"), {
      method: "DELETE",
    });
  }

  private filterIsDeletedPhotos(dto: IObservationDto): IObservationDto {
    return {
      ...dto,
      photos: dto.photos.filter((photoDto) => !photoDto.isDeleted),
    };
  }
}

export const observationsService = new ObservationsService();
